import { useEffect, useRef, memo } from 'react'
import { createNoise3D } from 'simplex-noise'
import alea from 'alea'

interface NoiseCanvasProps {
  color1?: string
  color2?: string
  scale?: number
  speed?: number
  className?: string
}

function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return [0, 0, 0]
  return [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
}

function NoiseCanvasInner({
  color1 = '#f40014',
  color2 = '#0a0000',
  scale = 0.7,
  speed = 200,
  className = '',
}: NoiseCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const prng = alea('pinnacle-noise')
    const noise3D = createNoise3D(prng)

    const rgb1 = hexToRgb(color1)
    const rgb2 = hexToRgb(color2)

    let animationId: number
    let startTime = performance.now()

    function resize() {
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      canvas.width = Math.ceil(rect.width / 4)
      canvas.height = Math.ceil(rect.height / 4)
    }

    resize()
    const resizeObserver = new ResizeObserver(resize)
    resizeObserver.observe(canvas)

    function render(time: number) {
      if (!canvas || !ctx) return

      const w = canvas.width
      const h = canvas.height
      if (w === 0 || h === 0) {
        animationId = requestAnimationFrame(render)
        return
      }

      const imageData = ctx.createImageData(w, h)
      const data = imageData.data
      const t = (time - startTime) / (speed * 33)

      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const nx = (x / w) * scale
          const ny = (y / h) * scale
          const v = (noise3D(nx, ny, t) + 1) / 2

          const idx = (y * w + x) * 4
          data[idx] = Math.floor(rgb2[0] + (rgb1[0] - rgb2[0]) * v)
          data[idx + 1] = Math.floor(rgb2[1] + (rgb1[1] - rgb2[1]) * v)
          data[idx + 2] = Math.floor(rgb2[2] + (rgb1[2] - rgb2[2]) * v)
          data[idx + 3] = 255
        }
      }

      ctx.putImageData(imageData, 0, 0)
      animationId = requestAnimationFrame(render)
    }

    animationId = requestAnimationFrame(render)

    return () => {
      cancelAnimationFrame(animationId)
      resizeObserver.disconnect()
    }
  }, [color1, color2, scale, speed])

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        imageRendering: 'auto',
      }}
    />
  )
}

const NoiseCanvas = memo(NoiseCanvasInner)
export default NoiseCanvas
